import React, { useState, useEffect } from 'react';
import { phoneValidation, applyPhoneMask } from '../../utils/phoneValidation';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  NavigateNext,
  Refresh,
  Info
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { 
  FestojoFormData, 
  ESTADOS_BRASIL, 
  generateSlug 
} from '../../types/festejo';
import { StepComponentProps } from './FestojoFormContainer';
import festojoService from '../../services/festojoService';

interface BasicInfoFormProps extends StepComponentProps {}

interface FormErrors {
  [key: string]: string;
}

const BasicInfoForm: React.FC<BasicInfoFormProps> = ({
  formData,
  onUpdate,
  onNext,
  isFirstStep,
  isLastStep
}) => {
  const [errors, setErrors] = useState<FormErrors>({});
  const [slugChecking, setSlugChecking] = useState<boolean>(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);

  // Validar campos obrigatórios
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Título deve ter pelo menos 3 caracteres';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Identificador é obrigatório';
    } else if (formData.slug.trim().length < 3) {
      newErrors.slug = 'Identificador deve ter pelo menos 3 caracteres';
    }

    if (!formData.padroeiro.trim()) {
      newErrors.padroeiro = 'Nome do padroeiro é obrigatório';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Descrição deve ter pelo menos 10 caracteres';
    }

    if (!formData.paroquia.trim()) {
      newErrors.paroquia = 'Nome da paróquia é obrigatório';
    }

    if (!formData.endereco.trim()) {
      newErrors.endereco = 'Endereço é obrigatório';
    }

    if (!formData.cidade.trim()) {
      newErrors.cidade = 'Cidade é obrigatória';
    }

    if (!formData.estado) {
      newErrors.estado = 'Estado é obrigatório';
    }

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Email deve ter um formato válido';
    }

    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = 'Telefone é obrigatório';
    } else {
      const phoneValidationResult = phoneValidation('Telefone inválido')(formData.contactPhone);
      if (phoneValidationResult !== true) {
        newErrors.contactPhone = 'Telefone inválido';
      }
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Data de início é obrigatória';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'Data de término é obrigatória';
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      if (startDate >= endDate) {
        newErrors.endDate = 'Data de término deve ser posterior à data de início';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Verificar disponibilidade do slug
  const checkSlugAvailability = async (slug: string) => {
    if (!slug || slug.length < 3) return;

    setSlugChecking(true);
    try {
      const response = await festojoService.checkSlugAvailability(slug);
      setSlugAvailable(response.data?.available || false);
    } catch (error) {
      console.error('Erro ao verificar slug:', error);
      setSlugAvailable(null);
    } finally {
      setSlugChecking(false);
    }
  };

  // Verificar slug quando mudar
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.slug) {
        checkSlugAvailability(formData.slug);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.slug]);

  // Gerar novo slug baseado no título
  const handleRegenerateSlug = () => {
    if (formData.title) {
      const newSlug = generateSlug(formData.title);
      onUpdate({ slug: newSlug });
    }
  };

  // Manipular mudanças nos campos
  const handleFieldChange = (field: keyof FestojoFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    onUpdate({ [field]: value });

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Manipular mudanças de data
  const handleDateChange = (field: 'startDate' | 'endDate') => (date: Date | null) => {
    if (date) {
      onUpdate({ [field]: date.toISOString() });
      
      // Limpar erro do campo
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
      }
    }
  };

  // Avançar para próxima etapa
  const handleNext = () => {
    if (validateForm() && slugAvailable !== false) {
      onNext();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Informações Básicas do Festejo
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Preencha as informações principais do seu festejo religioso
        </Typography>

        <Grid container spacing={3}>
          {/* Título */}
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Título do Festejo"
              value={formData.title}
              onChange={handleFieldChange('title')}
              error={!!errors.title}
              helperText={errors.title || 'Ex: Festa de São João Batista 2024'}
              required
            />
          </Grid>

          {/* Identificador/Slug */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Identificador (URL)"
              value={formData.slug}
              onChange={handleFieldChange('slug')}
              error={!!errors.slug || slugAvailable === false}
              helperText={
                errors.slug || 
                (slugChecking ? 'Verificando...' : 
                 slugAvailable === true ? 'Disponível' :
                 slugAvailable === false ? 'Já existe' : 
                 'Usado na URL do site')
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Gerar automaticamente">
                      <IconButton onClick={handleRegenerateSlug} size="small">
                        <Refresh />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
              required
            />
          </Grid>

          {/* Padroeiro */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nome do Padroeiro"
              value={formData.padroeiro}
              onChange={handleFieldChange('padroeiro')}
              error={!!errors.padroeiro}
              helperText={errors.padroeiro || 'Ex: São João Batista'}
              required
            />
          </Grid>

          {/* Datas */}
          <Grid item xs={12} md={3}>
            <DatePicker
              label="Data de Início"
              value={formData.startDate ? new Date(formData.startDate) : null}
              onChange={handleDateChange('startDate')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.startDate,
                  helperText: errors.startDate,
                  required: true
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <DatePicker
              label="Data de Término"
              value={formData.endDate ? new Date(formData.endDate) : null}
              onChange={handleDateChange('endDate')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.endDate,
                  helperText: errors.endDate,
                  required: true
                }
              }}
            />
          </Grid>

          {/* Descrição */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Descrição do Festejo"
              value={formData.description}
              onChange={handleFieldChange('description')}
              error={!!errors.description}
              helperText={errors.description || 'Descreva o festejo, suas tradições e importância para a comunidade'}
              required
            />
          </Grid>

          {/* Informações da Paróquia */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Informações da Paróquia
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nome da Paróquia"
              value={formData.paroquia}
              onChange={handleFieldChange('paroquia')}
              error={!!errors.paroquia}
              helperText={errors.paroquia}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Endereço"
              value={formData.endereco}
              onChange={handleFieldChange('endereco')}
              error={!!errors.endereco}
              helperText={errors.endereco}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Cidade"
              value={formData.cidade}
              onChange={handleFieldChange('cidade')}
              error={!!errors.cidade}
              helperText={errors.cidade}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.estado} required>
              <InputLabel>Estado</InputLabel>
              <Select
                value={formData.estado}
                onChange={handleFieldChange('estado')}
                label="Estado"
              >
                {ESTADOS_BRASIL.map((estado) => (
                  <MenuItem key={estado.value} value={estado.value}>
                    {estado.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.estado && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.estado}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Link do Mapa (opcional) */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Link do Google Maps (opcional)"
              value={formData.mapLink}
              onChange={handleFieldChange('mapLink')}
              helperText="Link para localização no Google Maps"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Info color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {/* Informações de Contato */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Informações de Contato
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="email"
              label="Email de Contato"
              value={formData.contactEmail}
              onChange={handleFieldChange('contactEmail')}
              error={!!errors.contactEmail}
              helperText={errors.contactEmail}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Telefone de Contato"
              placeholder="(11) 98765-4321"
              value={formData.contactPhone}
              onChange={(e) => {
                const formatted = applyPhoneMask(e.target.value);
                handleFieldChange('contactPhone')({ target: { value: formatted } });
              }}
              error={!!errors.contactPhone}
              helperText={errors.contactPhone || "Ex: (11) 98765-4321 para celular ou (11) 3456-7890 para fixo"}
              required
              inputProps={{
                maxLength: 15, // (11) 98765-4321
              }}
            />
          </Grid>
        </Grid>

        {/* Alertas */}
        {slugAvailable === false && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Este identificador já está em uso. Por favor, escolha outro.
          </Alert>
        )}

        {/* Botões de navegação */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={slugChecking || slugAvailable === false}
            endIcon={<NavigateNext />}
            size="large"
          >
            Próximo
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default BasicInfoForm;

